import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useBusinessUnit } from "../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Center,
  Button,
  Icon,
  Text,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  CheckboxGroup,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  VStack,
  useToast,
  HStack,
  Avatar,
  SimpleGrid,
  Badge,
  Divider,
  Tooltip,
  FormHelperText,
  Alert,
  AlertIcon,
  CloseButton,
  Skeleton,
  Progress,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaPaperPlane, FaFileAlt, FaRobot, FaMicrophone, FaQuestionCircle, FaChartBar, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { downloadConsultantCv } from "../../api/consultantsApi";
import { PageHeader, Panel, StatusBadge, MiniIconButton, FilterSearch, EmptyState } from "../../components/Portal/ui";

// Compact button props matching the dense template scale (mirrors Settings).
const primaryBtnProps = {
  variant: "primary",
  size: "sm",
  h: "32px",
  px: "14px",
  fontSize: "12px",
};

// Tiny uppercase form label, template `.label` / FormLabel scale.
const formLabelProps = {
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "portal.muted",
  sx: { marginBottom: "6px !important" },
};

// 31px-tall select sized to the template controls.
const selectProps = {
  size: "sm",
  height: "31px",
  lineHeight: "31px",
  sx: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    lineHeight: "31px !important",
    height: "31px !important",
  },
};

// Map a raw deliverable status to a StatusBadge colorScheme.
const statusSchemeFor = (statusRaw, isProcessing, hasAnyDeliverable) => {
  if (isProcessing) return "blue";
  if (["ERROR", "FAILED"].includes(statusRaw)) return "red";
  if (statusRaw === "COMPLETED") return "green";
  if (hasAnyDeliverable) return "green";
  return "gray";
};

// Combined page: left = documents list, right = CV submission form
const AsapCV = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const [searchParams, setSearchParams] = useSearchParams();

  // Linked consultant (passed from consultant detail modal)
  const [linkedConsultantId, setLinkedConsultantId] = useState(() => {
    const id = searchParams.get("consultantId");
    return id ? parseInt(id, 10) : null;
  });
  const [linkedConsultantName, setLinkedConsultantName] = useState(
    () => searchParams.get("consultantName") || null
  );

  const unlinkConsultant = () => {
    setLinkedConsultantId(null);
    setLinkedConsultantName(null);
    searchParams.delete("consultantId");
    searchParams.delete("consultantName");
    searchParams.delete("loadCv");
    setSearchParams(searchParams, { replace: true });
  };

  const shouldLoadCv = searchParams.get("loadCv") === "1";
  const cvLoadedRef = useRef(false);
  const [loadingConsultantCv, setLoadingConsultantCv] = useState(shouldLoadCv);

  // BU context must be initialized before any effects using it
  const { user } = useAuth();
  const { selectedBusinessUnit, selectedBusinessUnitId, isLoaded: isBUContextLoaded, businessUnits } = useBusinessUnit();

  // Documents state
  const [groups, setGroups] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState("");
  // Inline name edit state
  const [editingNameReqId, setEditingNameReqId] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  // Compute monthly CV counts for chart (last 6 months)
  const [monthData, setMonthData] = useState([]);
  // Search & pagination state
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalItems, setTotalItems] = useState(null);

  useEffect(() => {
    const counts = {};
    (groups || []).forEach((g) => {
      const d = new Date(g.created_at);
      const key = `${d.getFullYear()}-${d.getMonth()}`; // e.g., 2024-7
      counts[key] = (counts[key] || 0) + 1;
    });
    // Build last 6 months array regardless of data
    const arr = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      arr.push({
        label: date.toLocaleString('default', { month: 'short' }),
        value: counts[key] || 0,
      });
    }
    setMonthData(arr);
  }, [groups]);

  // Debounce search input and reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch documents function - reusable for both initial load and periodic updates
  const fetchDocuments = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setIsLoadingDocs(true);
    }
    try {
      const config = {
        params: {
          groupType: "cv",
          search: search || undefined,
          page,
          pageSize,
        },
        ...(selectedBusinessUnitId ? { headers: { "X-Business-Unit-Id": selectedBusinessUnitId } } : {}),
      };
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/documents`,
        config
      );
      const payload = response.data;
      let newGroups = [];
      let total = null;
      if (Array.isArray(payload)) {
        newGroups = payload;
      } else if (payload && Array.isArray(payload.items)) {
        newGroups = payload.items;
        total = typeof payload.total === "number" ? payload.total : (typeof payload.count === "number" ? payload.count : null);
      } else if (payload && Array.isArray(payload.data)) {
        newGroups = payload.data;
        total = typeof payload.total === "number" ? payload.total : (typeof payload.count === "number" ? payload.count : null);
      }
      setTotalItems(total);
      const computedHasNext = typeof total === "number" ? (page * pageSize) < total : (newGroups.length === pageSize);
      setHasNextPage(computedHasNext);

      // Smart comparison: only update if there are actual changes
      if (isInitialLoad) {
        setGroups(newGroups);
      } else {
        // For periodic updates, compare with existing groups
        setGroups(prevGroups => {
          // Check if there are new groups or if existing groups have been updated
          const hasNewGroups = newGroups.length !== prevGroups.length;
          const hasUpdatedGroups = newGroups.some(newGroup => {
            const existingGroup = prevGroups.find(prevGroup => prevGroup.id === newGroup.id);
            return !existingGroup ||
                   existingGroup.updated_at !== newGroup.updated_at ||
                   existingGroup.created_at !== newGroup.created_at;
          });

          // Only update if there are actual changes
          if (hasNewGroups || hasUpdatedGroups) {
            return newGroups;
          }
          return prevGroups; // No changes, keep existing state
        });
      }
    } catch (error) {
      console.error("Failed to fetch documents", error);
      // Only show toast for initial load errors, not for periodic updates
      if (isInitialLoad) {
        const isExpired = error?.response?.status === 402;
        toast({
          title: isExpired
            ? t("asapCV.toasts.trialExpiredTitle")
            : t("asapCV.toasts.fetchErrorTitle"),
          description: isExpired
            ? t("asapCV.toasts.trialExpiredDescription")
            : error.response?.data?.message || t("asapCV.toasts.fetchErrorDescription"),
          status: isExpired ? "warning" : "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      if (isInitialLoad) {
        setIsLoadingDocs(false);
      }
    }
  };

  // Initial fetch documents only after BU context is ready; include BU header when available
  useEffect(() => {
    if (!isBUContextLoaded) return; // wait for BU context
    // If user has BUs, wait until one is selected; if user has none, proceed without header
    if (Array.isArray(selectedBusinessUnitId) ? selectedBusinessUnitId.length === 0 : false) return;
    fetchDocuments(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, isBUContextLoaded, selectedBusinessUnitId, page, pageSize, search]);

  // Set up periodic fetching every 45 seconds
  useEffect(() => {
    if (!isBUContextLoaded) return; // wait for BU context
    // If user has BUs, wait until one is selected; if user has none, proceed without header
    if (Array.isArray(selectedBusinessUnitId) ? selectedBusinessUnitId.length === 0 : false) return;

    const interval = setInterval(() => {
      fetchDocuments(false); // false = not initial load
    }, 45000); // 45 seconds

    // Cleanup interval on unmount or dependency change
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId, page, pageSize, search]);

  // Auto-load consultant's stored CV when navigated from consultant detail
  useEffect(() => {
    if (!shouldLoadCv || !linkedConsultantId || cvLoadedRef.current) return;
    if (!isBUContextLoaded) return;
    cvLoadedRef.current = true;
    setLoadingConsultantCv(true);
    (async () => {
      try {
        const response = await downloadConsultantCv(linkedConsultantId, {
          buId: selectedBusinessUnitId,
        });
        const blob = response.data;
        const dispo = response.headers["content-disposition"] || "";
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(dispo);
        const serverFilename = match ? match[1].replace(/["']/g, "") : null;
        const fallbackName = `${linkedConsultantName || "consultant"}_cv.pdf`;
        const filename = serverFilename || fallbackName;
        const file = new File([blob], filename, { type: blob.type || "application/pdf" });
        setFiles((prev) => (prev.length === 0 ? [file] : prev));
        toast({
          title: t("asapCV.toasts.cvAutoLoaded"),
          status: "info",
          duration: 3000,
        });
      } catch (e) {
        console.error("Failed to auto-load consultant CV", e);
      } finally {
        setLoadingConsultantCv(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadCv, linkedConsultantId, isBUContextLoaded]);

  // No modal anymore; downloads are triggered directly from the card icons

  const handleKindDownload = async (reqId, kind, filenameBase) => {
    setDownloadingKey(`${reqId}:${kind}`);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/documents/group/${reqId}/download?kind=${kind}`,
        { responseType: "blob" }
      );

      // response.data is already a Blob with correct MIME type
      const blob = response.data;

      // Try to extract filename from Content-Disposition header
      const dispo = response.headers["content-disposition"] || "";
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(dispo);
      const serverFilename = match ? match[1].replace(/["']/g, "") : null;

      // Helper map: mime → file extension
      const typeToExt = (t) => {
        if (t === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return ".docx";
        if (t === "application/vnd.openxmlformats-officedocument.presentationml.presentation") return ".pptx";
        if (t === "application/pdf") return ".pdf";
        if (t === "text/plain") return ".txt";
        return "";
      };

      const fallbackName = `${filenameBase || "document"}_${kind}${typeToExt(blob.type)}`;
      const finalName = serverFilename || fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", finalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download document", error);
      let description = t("asapCV.toasts.downloadErrors.generic");
      if (error.response) {
        if (error.response.status === 400)
          description = t("asapCV.toasts.downloadErrors.notReady");
        else if (error.response.status === 403)
          description = t("asapCV.toasts.downloadErrors.forbidden");
        else if (error.response.status === 404)
          description = t("asapCV.toasts.downloadErrors.notFound");
        else if (error.response.status === 402)
          description = t("asapCV.toasts.trialExpiredDescription");
      }
      toast({
        title: t("asapCV.toasts.downloadFailedTitle"),
        description,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadingKey("");
    }
  };

  const beginEditName = (group) => {
    setEditingNameReqId(group.req_id);
    setEditFirstName(group.candidate_first_name || "");
    setEditLastName(group.candidate_last_name || "");
  };

  const cancelEditName = () => {
    setEditingNameReqId("");
    setEditFirstName("");
    setEditLastName("");
  };

  const saveEditedName = async (reqId) => {
    const body = {};
    if (editFirstName.trim()) body.firstName = editFirstName.trim();
    if (editLastName.trim()) body.lastName = editLastName.trim();
    if (!body.firstName && !body.lastName) {
      toast({
        title: t("asapCV.toasts.nameEdit.missingFields"),
        status: "warning",
      });
      return;
    }
    setIsSavingName(true);
    try {
      const config = selectedBusinessUnitId ? { headers: { "X-Business-Unit-Id": selectedBusinessUnitId } } : undefined;
      const res = await axios.patch(
        `${process.env.REACT_APP_BASE_API_URL}/documents/group/${reqId}/candidate-name`,
        body,
        config
      );
      const updated = res.data;
      setGroups((prev) =>
        (prev || []).map((g) =>
          g.req_id === reqId
            ? {
                ...g,
                candidate_first_name: updated?.candidate_first_name ?? body.firstName ?? g.candidate_first_name,
                candidate_last_name: updated?.candidate_last_name ?? body.lastName ?? g.candidate_last_name,
                updated_at: updated?.updated_at || g.updated_at,
              }
            : g
        )
      );
      toast({
        title: t("asapCV.toasts.nameEdit.success"),
        status: "success",
      });
      cancelEditName();
    } catch (error) {
      let description = t("asapCV.toasts.nameEdit.genericError");
      const code = error?.response?.status;
      if (code === 400) description = t("asapCV.toasts.nameEdit.emptyUpdate");
      else if (code === 403)
        description = t("asapCV.toasts.nameEdit.forbidden");
      else if (code === 404)
        description = t("asapCV.toasts.nameEdit.notFound");
      else if (code === 402)
        description = t("asapCV.toasts.nameEdit.trialExpired");
      toast({
        title: t("asapCV.toasts.nameEdit.errorTitle"),
        description,
        status: "error",
      });
    } finally {
      setIsSavingName(false);
    }
  };

  // CV form state (adapted from CVModule)
  const defaultCompanyCode = selectedBusinessUnit?.unit_code || user?.companyCode || "";
  const [companyCode] = useState(defaultCompanyCode);
  const [files, setFiles] = useState([]);
  const [jobDescFile, setJobDescFile] = useState(null);
  const [language, setLanguage] = useState("francais");
  const [jobType, setJobType] = useState("");
  const [deliverables, setDeliverables] = useState({
    cvOriginal: false,
    cvVersionIA: false,
    scoringAppelOffre: false,
    guideOral: false,
    questionsEntretiens: false,
    testerCandidat: false,
  });
  const [deliverableEnabled, setDeliverableEnabled] = useState({
    cvOriginal: true,
    cvVersionIA: true,
    scoringAppelOffre: true,
    guideOral: true,
    questionsEntretiens: true,
    testerCandidat: true,
  });
  const [candidateEmails, setCandidateEmails] = useState("");

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const fileInputRef = useRef(null);
  const jobDescInputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [jobDescDragActive, setJobDescDragActive] = useState(false);

  const hasSelectedBU = Boolean(selectedBusinessUnitId);
  const noBUAvailable = isBUContextLoaded && (!businessUnits || businessUnits.length === 0);

  const isAllowedCVFile = (file) => {
    if (!file) return false;
    const name = (file.name || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";
    const mime = (file.type || "").toLowerCase();

    // Some browsers may leave `file.type` empty; support both mime + extension.
    const allowedExt = ext === "pdf" || ext === "docx";
    const allowedMime =
      mime === "application/pdf" ||
      mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    return allowedExt || allowedMime;
  };

  const showInvalidCVToast = (invalidFiles) => {
    const names = (invalidFiles || []).map((f) => f?.name).filter(Boolean);
    toast({
      title: t("asapCV.toasts.submit.invalidFileTypeTitle", {
        defaultValue: "Unsupported file type",
      }),
      description: names.length
        ? t("asapCV.toasts.submit.invalidFileTypeDescriptionWithList", {
            defaultValue:
              "Rejected files: {{list}}. Accepted formats: PDF, DOCX.",
            list: names.join(", "),
          })
        : t("asapCV.toasts.submit.invalidFileTypeDescription", {
            defaultValue: "Accepted formats: PDF, DOCX.",
          }),
      status: "error",
      duration: 6000,
      isClosable: true,
    });
  };

  const handleDeliverableToggle = (name) => (e) => {
    setDeliverables({ ...deliverables, [name]: e.target.checked });
  };
  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files);
    const valid = selected.filter(isAllowedCVFile);
    const invalid = selected.filter((f) => !isAllowedCVFile(f));
    if (invalid.length) showInvalidCVToast(invalid);
    if (valid.length) setFiles((prev) => [...prev, ...valid]);
    event.target.value = null;
  };
  const removeFile = (file) => setFiles((prev) => prev.filter((f) => f !== file));
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const valid = droppedFiles.filter(isAllowedCVFile);
      const invalid = droppedFiles.filter((f) => !isAllowedCVFile(f));
      if (invalid.length) showInvalidCVToast(invalid);
      if (valid.length) setFiles((prev) => [...prev, ...valid]);
    }
  };
  const handleJobDescDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setJobDescDragActive(true);
    else if (e.type === "dragleave") setJobDescDragActive(false);
  };
  const handleJobDescDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setJobDescDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (!isAllowedCVFile(file)) {
        showInvalidCVToast([file]);
        return;
      }
      setJobDescFile(file);
    }
  };
  const validateEmails = () => {
    if (!deliverables.testerCandidat) return true;
    if (!candidateEmails.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = candidateEmails
      .split(",")
      .map((e) => e.trim())
      .filter((e) => !!e && !emailRegex.test(e));
    if (invalid.length) {
      toast({
        title: t("asapCV.toasts.submit.invalidEmails", {
          list: invalid.join(", "),
        }),
        status: "error",
      });
      return false;
    }
    return true;
  };
  const isAnyDeliverableSelected = () => Object.values(deliverables).some(Boolean);

  // Fetch available CV templates only after BU context is ready; include BU header when available
  useEffect(() => {
    if (!isBUContextLoaded) return;
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/templates`,
          selectedBusinessUnitId ? { headers: { "X-Business-Unit-Id": selectedBusinessUnitId } } : undefined
        );
        const list = Array.isArray(res.data) ? res.data.filter((t) => (t.doc_type || "").toUpperCase() === "CV") : [];
        setTemplates(list);
      } catch (e) {
        console.error("Failed to load templates", e);
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  // Select first template by default when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(String(templates[0].id));
    }
  }, [templates, selectedTemplateId]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!hasSelectedBU) {
      toast({
        title: noBUAvailable
          ? t("asapCV.alerts.noBusinessUnitTitle")
          : t("asapCV.alerts.businessUnitRequiredTitle"),
        description: noBUAvailable
          ? t("asapCV.alerts.noBusinessUnitDescription")
          : t("asapCV.alerts.businessUnitRequiredDescription"),
        status: "warning",
      });
      return;
    }
    if (!files.length) {
      toast({ title: t("asapCV.toasts.submit.noFiles"), status: "error" });
      return;
    }
    // Guardrail: prevent submitting if any invalid CV slipped in.
    const invalidExisting = files.filter((f) => !isAllowedCVFile(f));
    if (invalidExisting.length) {
      showInvalidCVToast(invalidExisting);
      return;
    }
    if (!isAnyDeliverableSelected()) {
      toast({
        title: t("asapCV.toasts.submit.noDeliverables"),
        status: "error",
      });
      return;
    }
    if (!validateEmails()) return;

    setIsLoadingSubmit(true);
    try {
      const templateIdToUse = selectedTemplateId || (templates[0]?.id ? String(templates[0].id) : "");
      const requests = files.map((file) => {
        const formData = new FormData();
        formData.append("cvFile", file);
        if (jobDescFile) formData.append("jobDescFile", jobDescFile);

        formData.append("jobType", jobType);
        formData.append("language", language);
        Object.entries(deliverables).forEach(([key, value]) => formData.append(key, value));
        if (deliverables.testerCandidat && candidateEmails) formData.append("candidateEmails", candidateEmails);
        if (templateIdToUse) formData.append("templateId", templateIdToUse);
        if (linkedConsultantId) formData.append("consultantId", linkedConsultantId);
        return axios.post(`${process.env.REACT_APP_BASE_API_URL}/api/cv`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      });

      // Optional evaluation when testerCandidat enabled
      if (deliverables.testerCandidat && candidateEmails.trim()) {
        const evalForm = new FormData();
        if (files.length) evalForm.append("cvFile", files[0]);
        if (jobDescFile) evalForm.append("jobDescFile", jobDescFile);
        candidateEmails
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean)
          .forEach((email) => evalForm.append("candidateEmails", email));
        await axios.post(`${process.env.REACT_APP_BASE_API_URL}/api/prepare/evaluation`, evalForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      const results = await Promise.allSettled(requests);
      const successCount = results.filter((r) => r.status === "fulfilled" && r.value.status === 200).length;
      toast({
        title: t("asapCV.toasts.submit.summary", {
          success: successCount,
          total: results.length,
        }),
        status: successCount ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });
      // Refresh documents shortly after submit to show the new processing card
      if (successCount > 0) {
        setTimeout(() => {
          fetchDocuments(false);
        }, 1000);
      }
      if (successCount === results.length) {
        setFiles([]);
        setJobDescFile(null);
        setDeliverables({
          cvOriginal: false,
          cvVersionIA: false,
          scoringAppelOffre: false,
          guideOral: false,
          questionsEntretiens: false,
          testerCandidat: false,
        });
        setCandidateEmails("");
        setJobType("");

      }
    } catch (error) {
      console.error(error);
      const isExpired = error?.response?.status === 402;
      toast({
        title: isExpired
          ? t("asapCV.toasts.trialExpiredTitle")
          : t("asapCV.toasts.submit.errorTitle"),
        description: isExpired
          ? t("asapCV.toasts.trialExpiredDescription")
          : undefined,
        status: isExpired ? "warning" : "error",
      });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const isCVUploaded = files.length > 0;
  const isMissionUploaded = jobDescFile !== null;
  const isSubmitDisabled = !hasSelectedBU || !isCVUploaded || !selectedTemplateId || !isAnyDeliverableSelected();

  // ── Document card (left-panel list item) ──────────────────────────────
  const renderDocumentCard = (g) => {
    const fullName = `${g.candidate_first_name || ""} ${g.candidate_last_name || ""}`.trim() || g.req_id;
    const statusRaw = (g.status || g.processing_status || "").toString().toUpperCase();
    const hasAnyDeliverable = Boolean(g.regular_cv_url || g.ai_cv_url || g.oral_guideline_url || g.qa_url || g.ao_scoring_url);
    const isProcessing = ["QUEUED", "PENDING", "PROCESSING", "IN_PROGRESS", "STARTED"].includes(statusRaw) || (!statusRaw && !hasAnyDeliverable);
    const isDimmed = ["COMPLETED", "ERROR", "FAILED"].includes(statusRaw);
    const statusScheme = statusSchemeFor(statusRaw, isProcessing, hasAnyDeliverable);
    const statusLabel = statusRaw || (isProcessing ? "PROCESSING" : (hasAnyDeliverable ? "COMPLETED" : "PENDING"));
    const parseSkillsLocal = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
        } catch (_) {
          // not JSON, fall through
        }
        return String(raw).split(",").map((s) => s.trim()).filter(Boolean);
      }
      return [];
    };
    let techSkills = parseSkillsLocal(g.skills_technique);
    let projectSkills = parseSkillsLocal(g.skills_projet);
    let businessSkills = parseSkillsLocal(g.skills_metier);
    // Backward compatibility with legacy key_skills
    if (techSkills.length === 0 && projectSkills.length === 0 && businessSkills.length === 0) {
      techSkills = parseSkillsLocal(g.key_skills);
    }

    const deliverableActions = [
      { key: "regular_cv_url", label: t("asapCV.card.deliverables.originalCv"), kind: "regular", icon: FaFileAlt },
      { key: "ai_cv_url", label: t("asapCV.card.deliverables.aiCv"), kind: "ai", icon: FaRobot },
      { key: "oral_guideline_url", label: t("asapCV.card.deliverables.oralGuide"), kind: "oral", icon: FaMicrophone },
      { key: "qa_url", label: t("asapCV.card.deliverables.qa"), kind: "qa", icon: FaQuestionCircle },
      { key: "ao_scoring_url", label: t("asapCV.card.deliverables.aoScoring"), kind: "ao_scoring", icon: FaChartBar },
    ];

    return (
      <Box
        key={g.id}
        data-testid={`cv-doc-card-${g.id}`}
        borderWidth="1px"
        borderColor="portal.line"
        bg="portal.panel"
        borderRadius="10px"
        p="14px"
        transition="box-shadow .15s, background .15s"
        _hover={{ boxShadow: "kpi", bg: "portal.soft" }}
      >
        <HStack align="start" spacing="12px">
          <Avatar name={fullName} size="sm" bg="portal.active" color="portal.primary" />
          <Box flex="1" minW="0" opacity={isDimmed ? 0.55 : 1}>
            <HStack justify="space-between" align="start" spacing={2}>
              <Box minW="0">
                {editingNameReqId === g.req_id ? (
                  <HStack spacing={1} flexWrap="wrap">
                    <Input
                      size="xs"
                      h="28px"
                      placeholder={t("asapCV.card.edit.firstNamePlaceholder")}
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      width="120px"
                    />
                    <Input
                      size="xs"
                      h="28px"
                      placeholder={t("asapCV.card.edit.lastNamePlaceholder")}
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      width="120px"
                    />
                    <MiniIconButton
                      aria-label={t("asapCV.card.edit.saveAria")}
                      icon={<Icon as={FaSave} />}
                      color="portal.primary"
                      isLoading={isSavingName}
                      onClick={() => saveEditedName(g.req_id)}
                    />
                    <MiniIconButton
                      aria-label={t("asapCV.card.edit.cancelAria")}
                      icon={<Icon as={FaTimes} />}
                      onClick={cancelEditName}
                      isDisabled={isSavingName}
                    />
                  </HStack>
                ) : (
                  <>
                    <HStack spacing={1.5} align="center" sx={{ marginBottom: "2px !important" }}>
                      <Skeleton isLoaded={!isProcessing} fadeDuration={0.2} w="fit-content">
                        <Text
                          fontSize="13px"
                          fontWeight="700"
                          color="portal.text"
                          noOfLines={1}
                          sx={{ marginBottom: "0 !important" }}
                          data-testid={`cv-doc-name-${g.id}`}
                        >
                          {fullName}
                        </Text>
                      </Skeleton>
                      <Tooltip label={t("asapCV.card.editNameTooltip")} hasArrow>
                        <MiniIconButton
                          aria-label={t("asapCV.card.editNameTooltip")}
                          icon={<Icon as={FaEdit} />}
                          onClick={() => beginEditName(g)}
                          isDisabled={isProcessing}
                        />
                      </Tooltip>
                    </HStack>
                    <Skeleton isLoaded={!isProcessing} fadeDuration={0.2} w={{ base: "60%", md: "50%" }}>
                      <Text fontSize="11px" color="portal.muted" noOfLines={1} sx={{ marginBottom: "0 !important" }}>
                        {g.candidate_position || t("asapCV.card.unknownPosition")}
                      </Text>
                    </Skeleton>
                  </>
                )}
              </Box>
              <VStack spacing="6px" align="end" flexShrink={0}>
                <StatusBadge label={statusLabel} colorScheme={statusScheme} />
                <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                  {g.candidate_age
                    ? t("asapCV.card.ageLabel", { count: g.candidate_age })
                    : t("asapCV.card.ageNA")}
                </Text>
              </VStack>
            </HStack>

            {(techSkills.length > 0 || projectSkills.length > 0 || businessSkills.length > 0) && (
              <VStack align="start" spacing="6px" mt="10px">
                {isProcessing ? (
                  <>
                    <Skeleton isLoaded={false} h="16px" w="80%" borderRadius="md" />
                    <Skeleton isLoaded={false} h="16px" w="60%" borderRadius="md" />
                  </>
                ) : (
                  <>
                    {techSkills.length > 0 && (
                      <Wrap spacing="6px">
                        {techSkills.slice(0, 6).map((s, idx) => (
                          <WrapItem key={`tech-${idx}`}>
                            <Badge colorScheme="purple" variant="subtle">{s}</Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                    {projectSkills.length > 0 && (
                      <Wrap spacing="6px">
                        {projectSkills.slice(0, 6).map((s, idx) => (
                          <WrapItem key={`proj-${idx}`}>
                            <Badge colorScheme="orange" variant="subtle">{s}</Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                    {businessSkills.length > 0 && (
                      <Wrap spacing="6px">
                        {businessSkills.slice(0, 6).map((s, idx) => (
                          <WrapItem key={`metier-${idx}`}>
                            <Badge colorScheme="green" variant="subtle">{s}</Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                  </>
                )}
              </VStack>
            )}

            <Divider my="10px" borderColor="portal.line" />
            <HStack spacing="8px" justify="space-between" align="center">
              <Text fontSize="11px" color="portal.muted" noOfLines={1} sx={{ marginBottom: "0 !important" }}>
                {t("asapCV.card.updatedAt", {
                  date: g.updated_at
                    ? new Date(g.updated_at).toLocaleString()
                    : new Date(g.created_at).toLocaleString(),
                })}
              </Text>
              <HStack spacing="6px" flexShrink={0}>
                {deliverableActions.map(({ key, label, kind, icon }) =>
                  g[key] ? (
                    <Tooltip key={kind} label={label} placement="top" hasArrow>
                      <MiniIconButton
                        aria-label={label}
                        icon={<Icon as={icon} />}
                        color="portal.primary"
                        isDisabled={isProcessing}
                        onClick={() => handleKindDownload(g.req_id, kind, fullName)}
                        isLoading={downloadingKey === `${g.req_id}:${kind}`}
                      />
                    </Tooltip>
                  ) : null
                )}
              </HStack>
            </HStack>
            {isProcessing && (
              <Progress mt="10px" size="xs" colorScheme="green" isIndeterminate borderRadius="999px" />
            )}
          </Box>
        </HStack>
      </Box>
    );
  };

  // ── Upload dropzone ───────────────────────────────────────────────────
  const renderDropzone = ({ active, onClick, onDragEnter, onDragLeave, onDragOver, onDrop, primaryText, loading }) => (
    <Box
      border="1px dashed"
      borderColor={active ? "portal.primary" : "portal.line"}
      borderRadius="10px"
      p="16px"
      textAlign="center"
      minH="150px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={active ? "portal.active" : "portal.soft"}
      transition="all 0.15s"
      cursor="pointer"
      onClick={onClick}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      _hover={{ bg: "portal.active", borderColor: "portal.primary" }}
    >
      {loading ? (
        <VStack spacing={2}>
          <Spinner size="md" color="portal.primary" />
          <Text fontSize="12px" fontWeight="600" color="portal.primary" sx={{ marginBottom: "0 !important" }}>
            {t("asapCV.form.loadingConsultantCv", { defaultValue: "Loading CV from consultant profile..." })}
          </Text>
        </VStack>
      ) : (
        <VStack spacing="6px">
          <Icon as={FaCloudUploadAlt} boxSize="22px" color="portal.muted" />
          <Text fontSize="12px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }}>
            {primaryText}
          </Text>
          <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
            {t("asapCV.form.acceptedFormats")}
          </Text>
        </VStack>
      )}
    </Box>
  );

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
      <PageHeader
        moduleCode="CV"
        eyebrow={t("asapCV.eyebrow", "ASAP CV")}
        title={t("asapCV.pageTitle")}
      />

      <Box p="20px" flex="1" minH="0" display="flex" flexDirection="column">
        {linkedConsultantId && (
          <Alert status="info" mb="12px" borderRadius="10px" variant="left-accent">
            <AlertIcon />
            <Text fontSize="12px" flex={1} sx={{ marginBottom: "0 !important" }}>
              {t("asapCV.linkedConsultantBanner", {
                defaultValue: "This CV will be linked to {{name}}",
                name: linkedConsultantName || `Consultant #${linkedConsultantId}`,
              })}
            </Text>
            <CloseButton size="sm" onClick={unlinkConsultant} />
          </Alert>
        )}

        <Flex gap="12px" flex="1" minH="0" direction={{ base: "column", md: "row" }}>
          {/* Left — documents list */}
          <Box flex={{ base: "1 1 100%", md: "0 0 40%" }} minW={{ md: "320px" }} minH="0" display="flex">
            <Panel
              w="100%"
              h="100%"
              title={t("asapCV.documentsTitle")}
              count={totalItems != null ? totalItems : (isLoadingDocs ? undefined : groups.length)}
              filters={
                <FilterSearch
                  placeholder={t("asapCV.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  data-testid="cv-doc-search"
                />
              }
              bodyProps={{ p: "12px" }}
              footer={
                <Flex
                  minH="39px"
                  borderTopWidth="1px"
                  borderColor="portal.line"
                  align="center"
                  justify="space-between"
                  px="12px"
                  bg="portal.panel"
                >
                  <Text fontSize="11px" fontWeight="700" color="#667a89" sx={{ marginBottom: "0 !important" }}>
                    {t("asapCV.pagination.pageLabel", { page })}
                  </Text>
                  <HStack spacing="4px">
                    <Button
                      variant="secondary"
                      size="xs"
                      h="25px"
                      fontSize="11px"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      isDisabled={page <= 1}
                    >
                      {t("asapCV.pagination.previous")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="xs"
                      h="25px"
                      fontSize="11px"
                      onClick={() => setPage((p) => p + 1)}
                      isDisabled={!hasNextPage}
                    >
                      {t("asapCV.pagination.next")}
                    </Button>
                  </HStack>
                </Flex>
              }
            >
              <Box data-testid="cv-doc-list-block">
                {isLoadingDocs ? (
                  <Center py="40px">
                    <Spinner color="portal.primary" />
                  </Center>
                ) : groups.length === 0 ? (
                  <EmptyState
                    icon={<FaFileAlt />}
                    title={t("documents.noDocuments")}
                    hint={t("asapCV.emptyHint", "Upload a CV from the form to generate documents.")}
                  />
                ) : (
                  <VStack align="stretch" spacing="12px">
                    {groups.map(renderDocumentCard)}
                  </VStack>
                )}
              </Box>
            </Panel>
          </Box>

          {/* Right — CV submission form */}
          <Box flex="1" minH="0" display="flex">
            <Panel w="100%" h="100%" title={t("asapCV.form.panelTitle", "New CV request")} bodyProps={{ p: "16px" }}>
              {/* Monthly activity chart */}
              <Box mb="16px">
                <Heading
                  as="h3"
                  fontSize="11px"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.055em"
                  color="portal.muted"
                  sx={{ marginBottom: "8px !important" }}
                >
                  {t("asapCV.chart.title")}
                </Heading>
                <Stack direction="row" align="flex-end" spacing="10px" h="78px" pt="6px" overflow="hidden">
                  {(() => {
                    const maxValue = Math.max(...monthData.map((m) => m.value), 1);
                    const maxHeight = 60;
                    return monthData.map((m) => {
                      const barHeight = m.value === 0 ? 2 : Math.max(8, (m.value / maxValue) * maxHeight);
                      return (
                        <Box key={m.label} textAlign="center" flex="1">
                          <Tooltip label={t("asapCV.chart.tooltip", { count: m.value })} placement="top" hasArrow>
                            <Box
                              bgGradient="linear(180deg, #1E4D78 0%, #153B69 100%)"
                              w="100%"
                              borderRadius="6px"
                              h={`${barHeight}px`}
                              transition="height 0.3s"
                            />
                          </Tooltip>
                          <Text fontSize="10px" color="portal.muted" mt="4px" sx={{ marginBottom: "0 !important" }}>
                            {m.label}
                          </Text>
                        </Box>
                      );
                    });
                  })()}
                </Stack>
              </Box>

              <Divider mb="16px" borderColor="portal.line" />

              <form onSubmit={handleSubmit}>
                <Stack spacing="14px">
                  {isBUContextLoaded && !hasSelectedBU && (
                    <Alert status="warning" borderRadius="10px">
                      <AlertIcon />
                      <Text fontSize="12px" sx={{ marginBottom: "0 !important" }}>
                        {noBUAvailable
                          ? t("asapCV.alerts.noBusinessUnitDescription")
                          : t("asapCV.alerts.businessUnitRequiredDescription")}
                      </Text>
                    </Alert>
                  )}

                  {/* Upload areas side-by-side */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
                    <FormControl isRequired>
                      <FormLabel {...formLabelProps}>{t("asapCV.form.uploadCvLabel")}</FormLabel>
                      {renderDropzone({
                        active: dragActive,
                        onClick: () => fileInputRef.current?.click(),
                        onDragEnter: handleDrag,
                        onDragLeave: handleDrag,
                        onDragOver: handleDrag,
                        onDrop: handleDrop,
                        primaryText: t("asapCV.form.uploadCvDropText"),
                        loading: loadingConsultantCv,
                      })}
                      <Input ref={fileInputRef} type="file" multiple accept=".pdf,.docx" onChange={handleFilesSelected} display="none" />
                      {files.length > 0 && (
                        <Wrap spacing="6px" mt="8px">
                          {files.map((file) => (
                            <WrapItem key={file.name}>
                              <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="999px">
                                <TagLabel>{file.name}</TagLabel>
                                <TagCloseButton onClick={() => removeFile(file)} />
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      )}
                    </FormControl>
                    <FormControl>
                      <FormLabel {...formLabelProps}>{t("asapCV.form.uploadJobDescLabel")}</FormLabel>
                      {jobDescFile ? (
                        <Tag size="md" variant="subtle" colorScheme="blue" borderRadius="999px">
                          <TagLabel>{jobDescFile.name}</TagLabel>
                          <TagCloseButton onClick={() => setJobDescFile(null)} />
                        </Tag>
                      ) : (
                        <>
                          {renderDropzone({
                            active: jobDescDragActive,
                            onClick: () => jobDescInputRef.current?.click(),
                            onDragEnter: handleJobDescDrag,
                            onDragLeave: handleJobDescDrag,
                            onDragOver: handleJobDescDrag,
                            onDrop: handleJobDescDrop,
                            primaryText: t("asapCV.form.uploadJobDescDropText"),
                            loading: false,
                          })}
                          <Input type="file" accept=".pdf,.docx" ref={jobDescInputRef} onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (!isAllowedCVFile(file)) {
                              showInvalidCVToast([file]);
                              e.target.value = null;
                              return;
                            }
                            setJobDescFile(file);
                          }} display="none" />
                        </>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* Job options */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="14px">
                    <FormControl>
                      <FormLabel {...formLabelProps}>{t("asapCV.form.languageLabel")}</FormLabel>
                      <Select {...selectProps} value={language} onChange={(e) => setLanguage(e.target.value)}>
                        <option value="francais">{t("asapCV.form.languageOptions.fr")}</option>
                        <option value="anglais">{t("asapCV.form.languageOptions.en")}</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel {...formLabelProps}>{t("asapCV.form.jobTypeLabel")}</FormLabel>
                      <Select {...selectProps} value={jobType} onChange={(e) => setJobType(e.target.value)} placeholder={t("asapCV.form.jobTypePlaceholder")}>
                        <option value="moa">{t("asapCV.form.jobTypeOptions.moa")}</option>
                        <option value="chef-de-projet">{t("asapCV.form.jobTypeOptions.chef-de-projet")}</option>
                        <option value="product-owner">{t("asapCV.form.jobTypeOptions.product-owner")}</option>
                        <option value="consultant-fonctionnel">{t("asapCV.form.jobTypeOptions.consultant-fonctionnel")}</option>
                        <option value="consultant-technique">{t("asapCV.form.jobTypeOptions.consultant-technique")}</option>
                        <option value="architecte-si">{t("asapCV.form.jobTypeOptions.architecte-si")}</option>
                        <option value="expert-securite">{t("asapCV.form.jobTypeOptions.expert-securite")}</option>
                        <option value="pmo">{t("asapCV.form.jobTypeOptions.pmo")}</option>
                        <option value="finance-comptabilite">{t("asapCV.form.jobTypeOptions.finance-comptabilite")}</option>
                        <option value="rh-paie">{t("asapCV.form.jobTypeOptions.rh-paie")}</option>
                        <option value="supply-chain">{t("asapCV.form.jobTypeOptions.supply-chain")}</option>
                        <option value="marketing-commercial">{t("asapCV.form.jobTypeOptions.marketing-commercial")}</option>
                        <option value="gestion-risques">{t("asapCV.form.jobTypeOptions.gestion-risques")}</option>
                        <option value="juridique-fiscalite">{t("asapCV.form.jobTypeOptions.juridique-fiscalite")}</option>
                      </Select>
                    </FormControl>
                    <FormControl isDisabled={templatesLoading || templates.length === 0}>
                      <FormLabel {...formLabelProps}>{t("asapCV.form.templateLabel")}</FormLabel>
                      <Select {...selectProps} value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                        {templates.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                        ))}
                      </Select>
                      {selectedTemplateId && (
                        <FormHelperText fontSize="11px" color="portal.muted">
                          {(templates.find((t) => String(t.id) === String(selectedTemplateId))?.description) || ""}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* Deliverables */}
                  <Box>
                    <FormLabel {...formLabelProps}>{t("asapCV.form.deliverablesLabel")}</FormLabel>
                    <CheckboxGroup>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="8px">
                        <Checkbox size="sm" isChecked={deliverables.cvOriginal} isDisabled={!deliverableEnabled.cvOriginal || !isCVUploaded} onChange={handleDeliverableToggle("cvOriginal")}>
                          <Text as="span" fontSize="12px">{t("asapCV.form.deliverables.cvOriginal")}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={deliverables.cvVersionIA} isDisabled={!deliverableEnabled.cvVersionIA || !isCVUploaded} onChange={handleDeliverableToggle("cvVersionIA")}>
                          <Text as="span" fontSize="12px">{t("asapCV.form.deliverables.cvVersionIA")}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={deliverables.scoringAppelOffre} isDisabled={!deliverableEnabled.scoringAppelOffre || !isMissionUploaded} onChange={handleDeliverableToggle("scoringAppelOffre")}>
                          <Text as="span" fontSize="12px">{t("asapCV.form.deliverables.scoringAppelOffre")}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={deliverables.guideOral} isDisabled={!deliverableEnabled.guideOral || !isMissionUploaded} onChange={handleDeliverableToggle("guideOral")}>
                          <Text as="span" fontSize="12px">{t("asapCV.form.deliverables.guideOral")}</Text>
                        </Checkbox>
                        <Checkbox size="sm" isChecked={deliverables.questionsEntretiens} isDisabled={!deliverableEnabled.questionsEntretiens || !isMissionUploaded} onChange={handleDeliverableToggle("questionsEntretiens")}>
                          <Text as="span" fontSize="12px">{t("asapCV.form.deliverables.questionsEntretiens")}</Text>
                        </Checkbox>
                      </SimpleGrid>
                    </CheckboxGroup>
                  </Box>

                  {/* Candidate emails (shown only if testerCandidat used) */}
                  {deliverables.testerCandidat && (
                    <FormControl isRequired>
                      <FormLabel {...formLabelProps}>{t("asapCV.form.candidateEmailsLabel")}</FormLabel>
                      <Input
                        size="sm"
                        h="31px"
                        value={candidateEmails}
                        onChange={(e) => setCandidateEmails(e.target.value)}
                        placeholder={t("asapCV.form.candidateEmailsPlaceholder")}
                      />
                    </FormControl>
                  )}

                  <Button
                    {...primaryBtnProps}
                    leftIcon={<Icon as={FaPaperPlane} />}
                    type="submit"
                    onClick={handleSubmit}
                    isLoading={isLoadingSubmit}
                    loadingText={t("asapCV.form.submitLoading")}
                    w="fit-content"
                    isDisabled={isSubmitDisabled}
                    data-testid="cv-submit-btn"
                  >
                    {t("asapCV.form.submitLabel")}
                  </Button>
                </Stack>
              </form>
            </Panel>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default AsapCV;
